import React, { useState } from 'react';
import {
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Tooltip
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import 'yup-phone-lite';
import { errorMessage, userRoles, userTypes } from 'utils/constants';
import {
    Button,
    Grid,
    InputLabel,
    Stack,
    TextField,
    Box,
    Checkbox,
    FormControlLabel,
    FormHelperText,
    FormControl
} from '@mui/material';
import { TimePicker } from 'antd';
import Autocomplete from '../common/Autocomplete';
import { dayPickerOptions } from '../../utils/constants/day-picker';
import { DeleteFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import CountryCodeSelect from '../common/CountryCodeSelect';
import SnackbarNotify from 'utils/snackbar/SnackbarNotify';

const returnError = (errors, field) => errors[field];
const returnTouched = (touched, field) => touched[field];

const InviteGuestModal = ({ onClose, onSubmit, guestInvite, type, onDelete }) => {
    const isEdit = type === 'edit';
    const [selectedCountry, setSelectedCountry] = useState(null);

    const getInitialAccessDays = () => {
        if (isEdit && !guestInvite?.access_days) {
            return [{ label: 'Any Day', value: '' }];
        }
        if (guestInvite?.access_days) {
            return guestInvite.access_days.map(
                day => dayPickerOptions.find(opt => opt.value === day) || []
            );
        }
        return [];
    };

    const getInitialTimeRange = () => {
        if (!guestInvite?.access_start_time || !guestInvite?.access_end_time) {
            return null;
        }
        return [
            dayjs()
                .hour(guestInvite.access_start_time.hours)
                .minute(guestInvite.access_start_time.minutes),
            dayjs()
                .hour(guestInvite.access_end_time.hours)
                .minute(guestInvite.access_end_time.minutes)
        ];
    };

    const initialValues = {
        first_name: guestInvite?.first_name || '',
        last_name: guestInvite?.last_name || '',
        company_name: guestInvite?.company_name || '',
        phone: guestInvite?.phone || {
            code: 1,
            country: 'US',
            number: ''
        },
        access_days: getInitialAccessDays(),
        time_range: getInitialTimeRange(),
        anytime_access: !guestInvite?.access_start_time && !guestInvite?.access_end_time
    };

    const validationSchema = Yup.object().shape({
        first_name: Yup.string()
            .trim()
            .lowercase()
            .required(errorMessage.firstName.required),
        last_name: Yup.string()
            .trim()
            .lowercase()
            .required(errorMessage.lastName.required),
        company_name: Yup.string().trim().lowercase(),
        phone: Yup.object().shape({
            number: Yup.string().required('Phone number is required'),
            code: Yup.number(),
            country: Yup.string()
        }),
        access_days: Yup.array().min(1, errorMessage.accessDays.required),
        anytime_access: Yup.boolean(),
        time_range: Yup.mixed()
            .when('anytime_access', {
                is: false,
                then: schema => schema.required(errorMessage.timeRange.required),
                otherwise: schema => schema
            })
            .nullable()
    });

    const validatePhoneNumber = async values => {
        const { number, code, country } = values.phone;
        const phoneSchema = Yup.string().phone(country);

        if (!number) return { phone: { number: 'Phone number is required' } };

        try {
            phoneSchema.validateSync(`${code}${number}`);
            return {};
        } catch (error) {
            return {
                phone: { number: 'Phone number needs to be valid' }
            };
        }
    };

    const handleDelete = async () => {
        try {
            await onDelete(guestInvite);
            SnackbarNotify.success('Invitation removed.');
            onClose();
        } catch (error) {
            console.error('Error removing guest invite:', error);
            SnackbarNotify.error(
                `Error removing guest invite: ${error?.message || 'Unexpected error'}`
            );
        }
    };

    const handleSubmit = async values => {
        let accessEndTime = null;
        let accessStartTime = null;

        if (values.time_range && !values.anytime_access) {
            accessStartTime = {
                hours: values.time_range[0].$H,
                minutes: values.time_range[0].$m
            };
            accessEndTime = {
                hours: values.time_range[1].$H,
                minutes: values.time_range[1].$m
            };
        }

        const phone = {
            number: values.phone.number,
            code: Number.parseInt(values.phone.code),
            country: values.phone.country.trim()
        };

        let accessDays = null;
        if (!values.access_days.filter(day => day.label === 'Any Day').length) {
            accessDays = values.access_days.map(day => day.value);
        }

        const payload = {
            first_name: values.first_name.trim().toLowerCase(),
            last_name: values.last_name.trim().toLowerCase(),
            company_name: values.company_name.trim().toLowerCase(),
            phone: phone,
            access_start_time: accessStartTime,
            access_end_time: accessEndTime,
            access_days: accessDays,
            role: userRoles.GUEST,
            type: userTypes.SHORT_TERM
        };

        try {
            await onSubmit(payload);
            SnackbarNotify.success(
                isEdit
                    ? 'Successfully updated guest invite!'
                    : 'Invite sent — pending acceptance.'
            );
            onClose();
        } catch (error) {
            console.error(
                isEdit ? 'Error updating guest invite:' : 'Error adding guest:',
                error
            );
            SnackbarNotify.error(
                `${isEdit ? 'Error updating guest invite' : 'Error adding guest'}: ${
                    error?.message || 'Unexpected error'
                }`
            );
        }
    };

    return (
        <>
            <DialogTitle>{type === 'edit' ? 'Edit Guest' : 'Add New Guest'}</DialogTitle>
            <Divider />
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validate={validatePhoneNumber}
                validationSchema={validationSchema}
                validateOnChange
                enableReinitialize
            >
                {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    setFieldValue,
                    touched,
                    values
                }) => (
                    <>
                        <DialogContent sx={{ p: 2.5, maxHeight: '70vh' }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>First Name</InputLabel>
                                        <TextField
                                            required
                                            id="firstName"
                                            name="first_name"
                                            placeholder="First Name"
                                            fullWidth
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                                touched.first_name && !!errors.first_name
                                            }
                                            helperText={
                                                touched.first_name && errors.first_name
                                            }
                                            value={values.first_name}
                                            inputProps={{
                                                style: { textTransform: 'capitalize' }
                                            }}
                                            FormHelperTextProps={{
                                                sx: { mx: 0 }
                                            }}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Last Name</InputLabel>
                                        <TextField
                                            required
                                            id="lastName"
                                            name="last_name"
                                            placeholder="Last Name"
                                            fullWidth
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                                touched.last_name && !!errors.last_name
                                            }
                                            helperText={
                                                touched.last_name && errors.last_name
                                            }
                                            value={values.last_name}
                                            inputProps={{
                                                style: { textTransform: 'capitalize' }
                                            }}
                                            FormHelperTextProps={{
                                                sx: { mx: 0 }
                                            }}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Company Name</InputLabel>
                                        <TextField
                                            required
                                            id="companyName"
                                            name="company_name"
                                            placeholder="Company Name"
                                            fullWidth
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.company_name}
                                            error={
                                                touched.company_name &&
                                                !!errors.company_name
                                            }
                                            helperText={
                                                touched.company_name &&
                                                errors.company_name
                                            }
                                            inputProps={{
                                                style: { textTransform: 'capitalize' }
                                            }}
                                            FormHelperTextProps={{
                                                sx: { mx: 0 }
                                            }}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <Stack spacing={0.5}>
                                        <InputLabel htmlFor="phone.country">
                                            Country Code
                                        </InputLabel>
                                        <CountryCodeSelect
                                            label=""
                                            size="medium"
                                            value={selectedCountry}
                                            defaultRegion={values.phone?.country || 'US'}
                                            onChange={newCountry => {
                                                if (!newCountry) return;

                                                setSelectedCountry(newCountry);

                                                setFieldValue(
                                                    'phone.code',
                                                    String(newCountry.code).replace(
                                                        /\D/g,
                                                        ''
                                                    )
                                                );

                                                setFieldValue(
                                                    'phone.country',
                                                    newCountry.region
                                                );
                                            }}
                                            sx={{ width: '100%' }}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Phone Number</InputLabel>
                                        <TextField
                                            required
                                            id="phoneNumber"
                                            name="phone.number"
                                            placeholder="Phone Number"
                                            inputMode="numeric"
                                            onChange={e =>
                                                setFieldValue(
                                                    'phone.number',
                                                    e.target.value.replace(/\D/g, '')
                                                )
                                            }
                                            fullWidth
                                            onBlur={handleBlur}
                                            value={values.phone?.number}
                                            error={
                                                touched.phone?.number &&
                                                !!errors.phone?.number
                                            }
                                            helperText={
                                                touched.phone?.number &&
                                                errors.phone?.number
                                            }
                                            FormHelperTextProps={{
                                                sx: { mx: 0 }
                                            }}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Access Days</InputLabel>
                                        <FormControl error variant="standard">
                                            <Autocomplete
                                                multiple
                                                id="access-days"
                                                options={dayPickerOptions}
                                                getOptionLabel={option => option.label}
                                                value={values.access_days}
                                                filterSelectedOptions
                                                onChange={value => {
                                                    const lastElement =
                                                        value[value.length - 1];
                                                    if (lastElement?.value === '') {
                                                        setFieldValue('access_days', [
                                                            lastElement
                                                        ]);
                                                    } else {
                                                        const updatedValue = value.filter(
                                                            val => val.value !== ''
                                                        );
                                                        setFieldValue(
                                                            'access_days',
                                                            updatedValue
                                                        );
                                                    }
                                                }}
                                                placeholder={
                                                    !values.access_days?.length
                                                        ? 'Select Access Days'
                                                        : ''
                                                }
                                                error={
                                                    touched.access_days &&
                                                    !!errors.access_days
                                                }
                                            />
                                            {returnTouched(touched, 'access_days') &&
                                                !!returnError(errors, 'access_days') && (
                                                    <FormHelperText>
                                                        {returnError(
                                                            errors,
                                                            'access_days'
                                                        )}
                                                    </FormHelperText>
                                                )}
                                        </FormControl>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Access Times</InputLabel>
                                        <FormControl error variant="standard">
                                            <Box>
                                                <TimePicker.RangePicker
                                                    popupStyle={{ zIndex: 1300 }}
                                                    defaultValue={values.time_range}
                                                    use12Hours
                                                    allowEmpty
                                                    id="time-range"
                                                    onChange={value =>
                                                        setFieldValue('time_range', value)
                                                    }
                                                    className={`${
                                                        returnTouched(
                                                            touched,
                                                            'time_range'
                                                        ) &&
                                                        !!returnError(
                                                            errors,
                                                            'time_range'
                                                        )
                                                            ? 'is-invalid'
                                                            : ''
                                                    }`}
                                                    status={
                                                        returnTouched(
                                                            touched,
                                                            'time_range'
                                                        ) &&
                                                        !!returnError(
                                                            errors,
                                                            'time_range'
                                                        )
                                                            ? 'error'
                                                            : ''
                                                    }
                                                    disabled={values.anytime_access}
                                                    onBlur={handleBlur('time_range')}
                                                    format={'HH:mm A'}
                                                />
                                                <FormControlLabel
                                                    value="end"
                                                    control={
                                                        <Checkbox
                                                            checked={
                                                                values.anytime_access
                                                            }
                                                            onChange={() =>
                                                                setFieldValue(
                                                                    'anytime_access',
                                                                    !values.anytime_access
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Anytime Access"
                                                    labelPlacement="end"
                                                    sx={{ ml: 1 }}
                                                />
                                            </Box>
                                            {returnTouched(touched, 'time_range') &&
                                                !!returnError(errors, 'time_range') && (
                                                    <FormHelperText>
                                                        {returnError(
                                                            errors,
                                                            'time_range'
                                                        )}
                                                    </FormHelperText>
                                                )}
                                        </FormControl>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <Divider />
                        <DialogActions sx={{ p: 2.5 }}>
                            <Grid
                                container
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Grid item>
                                    {isEdit && onDelete && (
                                        <Tooltip
                                            title="Delete Guest Invite"
                                            placement="top"
                                        >
                                            <IconButton
                                                size="large"
                                                color="error"
                                                onClick={handleDelete}
                                            >
                                                <DeleteFilled />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Grid>
                                <Grid item>
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                    >
                                        <Button color="error" onClick={onClose}>
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            onClick={handleSubmit}
                                        >
                                            {type === 'edit'
                                                ? 'Update Guest Invite'
                                                : 'Invite Guest'}
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </DialogActions>
                    </>
                )}
            </Formik>
        </>
    );
};

export default InviteGuestModal;
